import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Collapsible } from '@cbhq/cds-web/collapsible/Collapsible';
import { useA11yControlledVisibility } from '@cbhq/cds-web/hooks/useA11yControlledVisibility';
import { Icon, NavigationIcon, NavigationIconProps } from '@cbhq/cds-web/icons';
import { LogoMark } from '@cbhq/cds-web/icons/LogoMark';
import { Divider, HStack, Spacer, VStack } from '@cbhq/cds-web/layout';
import { Sidebar } from '@cbhq/cds-web/navigation/Sidebar';
import { SidebarItem } from '@cbhq/cds-web/navigation/SidebarItem';
import { Pressable } from '@cbhq/cds-web/system';
import { TextCaption, TextHeadline } from '@cbhq/cds-web/typography';

import CloudLogoWordmark from ':cloud/assets/Brand/CDPLogoWordmark';
import { NavItem } from ':cloud/config/navigation_DEPRECATED';
import { useAppState } from ':cloud/contexts/AppStateContext';
import { useNavigationContext } from ':cloud/contexts/NavigationContext';
import { useGetNavMenuItems } from ':cloud/hooks/useGetNavMenuItems_DEPRECATED';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';
import { AppRoute } from ':cloud/utils/routes';
import { hideOnMobileConfig, LEFT_NAV_WIDTH } from ':cloud/widgets/layout/constants';
import { SidebarItemWithChild } from ':cloud/widgets/navigation/SidebarItemWithChild';
import { ProjectSelector } from ':cloud/widgets/projects/ProjectSelector';

const hideOverflowXStyle: CSSProperties = { overflowX: 'hidden' };

type CustomSidebarItemProps = {
  title: string;
  active?: boolean;
  icon: NavigationIconProps['name'];
};

function CompressedSidebarItem({ title, icon, active }: CustomSidebarItemProps) {
  return (
    <HStack gap={2} spacingVertical={1} spacingHorizontal={2}>
      <NavigationIcon active={active} name={icon} size="m" />
      <TextHeadline color={active ? 'primary' : undefined} as="p">
        {title}
      </TextHeadline>
    </HStack>
  );
}

/** @deprecated Use LeftNav instead */
export function LeftNavWithProjects() {
  const [buildSectionCollapsed, { toggle: toggleBuildSection }] = useToggler(false);
  const [platformAPIsSectionCollapsed, { toggle: togglePlatformApisSection }] = useToggler(false);
  const [productAPIsSectionCollapsed, { toggle: toggleProductApisSection }] = useToggler(false);
  const { selectedProject } = useAppState();

  // Web only a11y configuration
  const {
    triggerAccessibilityProps: triggerAccessibilityPropsBuild,
    controlledElementAccessibilityProps: controlledElementAccessibilityPropsBuild,
  } = useA11yControlledVisibility(!buildSectionCollapsed, {
    accessibilityLabel: 'build-onchain-collapsible',
    hasPopupType: 'true',
  });
  const {
    triggerAccessibilityProps: triggerAccessibilityPropsProduct,
    controlledElementAccessibilityProps: controlledElementAccessibilityPropsProduct,
  } = useA11yControlledVisibility(!productAPIsSectionCollapsed, {
    accessibilityLabel: 'product-apis-collapsible',
    hasPopupType: 'true',
  });

  const [isNavCollapsed] = useState(false);
  const history = useHistory();
  const { activePage } = useNavigationContext();
  const { displayedNavItems, platformAPINavItems, productAPINavItems } = useGetNavMenuItems();

  // array index at which the products section starts
  // needs to be dynamic b/c different users see different nav items
  const productsIndex = displayedNavItems.findIndex((item) => item.to?.includes('products')) ?? 0;
  const primaryItems = displayedNavItems.slice(0, productsIndex);
  const buildOnchainItems = displayedNavItems.slice(productsIndex);
  const dashboardRoute = displayedNavItems.find((item) => item.id === 'project-dashboard');

  const sortedProductAPINavItems = useMemo(() => {
    return productAPINavItems.sort((a, b) => {
      // Sorts by internal items first, then by title
      const aIsOutbound = a.to?.startsWith('http');
      const bIsOutbound = b.to?.startsWith('http');

      if (aIsOutbound !== bIsOutbound) {
        return aIsOutbound ? 1 : -1;
      }

      return a.title.localeCompare(b.title);
    });
  }, [productAPINavItems]);

  const getIsItemActive = useCallback(
    (id) => {
      if (id === activePage.id) return true;
      if (id === 'dashboard' && activePage.id === 'projects') return true;
      return false;
    },
    [activePage],
  );

  const handlePress = useCallback(
    (navRoot: NavItem) => {
      const activeRoute = [
        ...displayedNavItems,
        ...platformAPINavItems,
        ...productAPINavItems,
      ].find((item) => item.id === navRoot.id);
      logClick(EventName.left_nav, { target: activeRoute?.title });
      if (!activeRoute) {
        return;
      }
      if (activeRoute.to?.startsWith('http')) {
        openUrlInNewWindow(activeRoute.to);
      } else if (navRoot.id === 'project-dashboard' && selectedProject) {
        history.push(`${AppRoute.Projects.Home}/${selectedProject.id}`);
      } else {
        history.push(activeRoute.to || AppRoute.Home);
      }
    },
    [displayedNavItems, history, platformAPINavItems, productAPINavItems, selectedProject],
  );

  const buildSection = useMemo(() => {
    return (
      <>
        {!isNavCollapsed && (
          <VStack spacingHorizontal={2} spacingVertical={1}>
            <HStack justifyContent="space-between" alignItems="center">
              <TextCaption color="foregroundMuted" as="p">
                Build Onchain
              </TextCaption>
              <IconButton
                name={buildSectionCollapsed ? 'caretDown' : 'caretUp'}
                variant="foregroundMuted"
                transparent
                onPress={toggleBuildSection}
                {...triggerAccessibilityPropsBuild}
              />
            </HStack>
          </VStack>
        )}
        <Collapsible
          collapsed={buildSectionCollapsed && !isNavCollapsed}
          {...controlledElementAccessibilityPropsBuild}
        >
          <VStack width="100%">
            {buildOnchainItems.map((item) => {
              return (
                <div key={`sidebar-item--${item.title}`} data-testid={`sidebar-item--${item.id}`}>
                  <SidebarItemWithChild
                    active={getIsItemActive(item.id)}
                    onPress={() => handlePress(item)}
                    as="button"
                    {...item}
                    title={item.title}
                  >
                    {item.to?.startsWith('http') && (
                      <HStack>
                        <Icon size="s" name="diagonalUpArrow" color="foreground" />
                      </HStack>
                    )}
                  </SidebarItemWithChild>
                </div>
              );
            })}
          </VStack>
        </Collapsible>
      </>
    );
  }, [
    buildOnchainItems,
    buildSectionCollapsed,
    controlledElementAccessibilityPropsBuild,
    getIsItemActive,
    handlePress,
    isNavCollapsed,
    toggleBuildSection,
    triggerAccessibilityPropsBuild,
  ]);

  const platformAPIsSection = useMemo(() => {
    return (
      <>
        {!isNavCollapsed && (
          <VStack spacingHorizontal={2} spacingVertical={1}>
            <HStack justifyContent="space-between" alignItems="center">
              <TextCaption color="foregroundMuted" as="p">
                Platform APIs
              </TextCaption>
              <IconButton
                name={platformAPIsSectionCollapsed ? 'caretDown' : 'caretUp'}
                variant="foregroundMuted"
                transparent
                onPress={togglePlatformApisSection}
                {...triggerAccessibilityPropsProduct}
              />
            </HStack>
          </VStack>
        )}
        <Collapsible
          collapsed={platformAPIsSectionCollapsed && !isNavCollapsed}
          {...controlledElementAccessibilityPropsProduct}
        >
          <VStack width="100%">
            {platformAPINavItems.map((item) => {
              return (
                <HStack
                  key={`sidebar-item--${item.title}`}
                  testID={`sidebar-item--${item.id}`}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SidebarItemWithChild
                    active={getIsItemActive(item.id)}
                    onPress={() => handlePress(item)}
                    as="button"
                    {...item}
                  >
                    {item?.to?.startsWith('http') && (
                      <HStack>
                        <Icon size="s" name="diagonalUpArrow" color="foreground" />
                      </HStack>
                    )}
                  </SidebarItemWithChild>
                </HStack>
              );
            })}
          </VStack>
        </Collapsible>
      </>
    );
  }, [
    controlledElementAccessibilityPropsProduct,
    getIsItemActive,
    handlePress,
    isNavCollapsed,
    platformAPINavItems,
    platformAPIsSectionCollapsed,
    togglePlatformApisSection,
    triggerAccessibilityPropsProduct,
  ]);

  const productAPIsSection = useMemo(() => {
    return (
      <>
        {!isNavCollapsed && (
          <VStack spacingHorizontal={2} spacingVertical={1}>
            <HStack justifyContent="space-between" alignItems="center">
              <TextCaption color="foregroundMuted" as="p">
                Product APIs
              </TextCaption>
              <IconButton
                name={productAPIsSectionCollapsed ? 'caretDown' : 'caretUp'}
                variant="foregroundMuted"
                transparent
                onPress={toggleProductApisSection}
                {...triggerAccessibilityPropsProduct}
              />
            </HStack>
          </VStack>
        )}
        <Collapsible
          collapsed={productAPIsSectionCollapsed && !isNavCollapsed}
          {...controlledElementAccessibilityPropsProduct}
        >
          <VStack width="100%">
            {sortedProductAPINavItems.map((item) => {
              return (
                <HStack
                  key={`sidebar-item--${item.title}`}
                  testID={`sidebar-item--${item.id}`}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SidebarItemWithChild
                    active={getIsItemActive(item.id)}
                    onPress={() => handlePress(item)}
                    as="button"
                    {...item}
                  >
                    {item?.to?.startsWith('http') && (
                      <HStack>
                        <Icon size="s" name="diagonalUpArrow" color="foreground" />
                      </HStack>
                    )}
                  </SidebarItemWithChild>
                </HStack>
              );
            })}
          </VStack>
        </Collapsible>
      </>
    );
  }, [
    controlledElementAccessibilityPropsProduct,
    getIsItemActive,
    handlePress,
    isNavCollapsed,
    sortedProductAPINavItems,
    productAPIsSectionCollapsed,
    toggleProductApisSection,
    triggerAccessibilityPropsProduct,
  ]);

  return (
    <VStack
      minWidth={LEFT_NAV_WIDTH}
      alignItems="center"
      justifyContent="center"
      zIndex={zIndex.navigation}
      responsiveConfig={hideOnMobileConfig}
      height="100vh"
      overflow="auto"
    >
      <Sidebar
        //  @ts-expect-error -- CDS types are incorrect
        position="relative"
        width="100%"
        logo={
          <Pressable
            as="a"
            onPress={() => handlePress(dashboardRoute as NavItem)}
            background="transparent"
          >
            {isNavCollapsed ? <LogoMark /> : <CloudLogoWordmark />}
          </Pressable>
        }
        collapsed={isNavCollapsed}
        renderEnd={() => <Spacer minVertical={4} />}
        style={hideOverflowXStyle}
        testID="side-navigation"
      >
        {!isNavCollapsed && (
          <VStack spacingBottom={2} spacingStart={2} gap={1}>
            <ProjectSelector />
            <Divider offsetHorizontal={6} />
          </VStack>
        )}
        <VStack width="100%">
          {primaryItems.map((item) => {
            return (
              <div key={`sidebar-item--${item.title}`} data-testid={`sidebar-item--${item.id}`}>
                <SidebarItem
                  {...item}
                  active={getIsItemActive(item.id)}
                  onPress={() => handlePress(item)}
                  as="button"
                  Component={CompressedSidebarItem}
                />
              </div>
            );
          })}
        </VStack>
        {buildSection}
        {platformAPIsSection}
        {productAPIsSection}
      </Sidebar>
    </VStack>
  );
}
